import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiHome, FiArrowLeft, FiSearch, FiHelpCircle, FiPhone, FiMail } = FiIcons;

const NotFound = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Simple search logic - redirect based on keywords
      const term = searchTerm.toLowerCase();
      if (term.includes('hosting') || term.includes('host')) {
        navigate('/services#hosting');
      } else if (term.includes('email')) {
        navigate('/services#email');
      } else if (term.includes('design') || term.includes('website')) {
        navigate('/services#design');
      } else if (term.includes('domain')) {
        navigate('/services#domains');
      } else if (term.includes('price') || term.includes('cost')) {
        navigate('/pricing');
      } else if (term.includes('contact') || term.includes('support')) {
        navigate('/contact');
      } else {
        navigate('/services');
      }
    }
  };

  const quickLinks = [
    {
      title: 'Web Hosting Services',
      description: 'Reliable hosting with 99.9% uptime',
      path: '/services#hosting',
      icon: FiSearch
    },
    {
      title: 'Email Hosting',
      description: 'Professional email solutions',
      path: '/services#email',
      icon: FiSearch
    },
    {
      title: 'Web Design',
      description: 'Custom website development',
      path: '/services#design',
      icon: FiSearch
    },
    {
      title: 'Domain Registration',
      description: 'Secure your perfect domain',
      path: '/services#domains',
      icon: FiSearch
    },
    {
      title: 'Pricing Plans',
      description: 'Transparent pricing options',
      path: '/pricing',
      icon: FiSearch
    },
    {
      title: 'Contact Support',
      description: 'Get help from our team',
      path: '/contact',
      icon: FiHelpCircle
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20 min-h-screen bg-gradient-to-br from-gray-50 to-white"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          {/* 404 Number */}
          <div className="mb-8">
            <h1 className="text-9xl font-bold text-primary-500 mb-4">404</h1>
            <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          </div>

          {/* Error Message */}
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Page not found
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Sorry, we couldn't find the page you're looking for. The page may have been moved, deleted, or you may have entered the wrong URL.
          </p>

          {/* Search Bar */}
          <div className="max-w-md mx-auto mb-12">
            <form onSubmit={handleSearch} className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search for hosting, email, design..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-700"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors duration-200"
              >
                Search
              </button>
            </form>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/"
              className="bg-primary-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-600 transition-colors duration-200 inline-flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiHome} className="w-5 h-5" />
              <span>Go to Homepage</span>
            </Link>
            <button
              onClick={() => window.history.back()}
              className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:border-primary-500 hover:text-primary-500 transition-colors duration-200 inline-flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              <span>Go Back</span>
            </button>
          </div>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-8 mb-12"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Looking for something specific?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickLinks.map((link, index) => (
              <Link
                key={index}
                to={link.path}
                className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 group"
              >
                <SafeIcon icon={link.icon} className="w-5 h-5 group-hover:text-primary-500" />
                <div className="text-left">
                  <div className="font-semibold">{link.title}</div>
                  <div className="text-sm text-gray-600 group-hover:text-primary-500">
                    {link.description}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Popular Pages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          <Link
            to="/services"
            className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 text-center"
          >
            <h4 className="font-semibold text-gray-900 mb-2">Our Services</h4>
            <p className="text-sm text-gray-600">Web hosting, email & design</p>
          </Link>
          <Link
            to="/pricing"
            className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 text-center"
          >
            <h4 className="font-semibold text-gray-900 mb-2">Pricing Plans</h4>
            <p className="text-sm text-gray-600">Transparent pricing options</p>
          </Link>
          <Link
            to="/about"
            className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 text-center"
          >
            <h4 className="font-semibold text-gray-900 mb-2">About Us</h4>
            <p className="text-sm text-gray-600">Local expertise since 2014</p>
          </Link>
          <Link
            to="/faq"
            className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 text-center"
          >
            <h4 className="font-semibold text-gray-900 mb-2">FAQ</h4>
            <p className="text-sm text-gray-600">Common questions answered</p>
          </Link>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-primary-50 rounded-2xl p-8 text-center"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Still can't find what you're looking for?
          </h3>
          <p className="text-gray-600 mb-6">
            Our support team is here to help you find exactly what you need.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <a
              href="tel:+27100070690"
              className="flex items-center space-x-2 bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors duration-200"
            >
              <SafeIcon icon={FiPhone} className="w-4 h-4" />
              <span>+27 10 007 0690</span>
            </a>
            <a
              href="mailto:support@primehost.co.za"
              className="flex items-center space-x-2 border border-primary-500 text-primary-500 px-6 py-3 rounded-lg font-medium hover:bg-primary-500 hover:text-white transition-colors duration-200"
            >
              <SafeIcon icon={FiMail} className="w-4 h-4" />
              <span>support@primehost.co.za</span>
            </a>
          </div>
          <p className="text-sm text-gray-600">
            Operating hours: 08:00 - 16:00 (Mon-Sun) | Emergency support: 24/7
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default NotFound;